import { formatDate } from '@/utils/format';
import { WARNING_LEVELS, WARNING_TAG_CONFIG } from '@/utils/logParser';
import type { Diary, Field, Season } from '@/types';

interface LogCardProps {
  log: Diary;
  field?: Field;
  season?: Season;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export function LogCard({ log, field, season, onView, onEdit, onDelete }: LogCardProps) {
  const wl = WARNING_LEVELS[log.warningLevel || 'none'];
  const tags = log.warningTags || [];
  const allImages = [...(log.hinhAnhList || []), ...(log.hinhAnh ? [log.hinhAnh] : [])];
  const hasVideo = !!log.videoUrl;
  const hasMaterialSuggestions = (log.materialSuggestions && log.materialSuggestions.length > 0);

  return (
    <div className={`bg-white rounded-xl border p-4 space-y-3 transition-colors ${
      log.isDeleted ? 'border-slate-100 opacity-60' :
      log.warningLevel === 'danger' ? 'border-red-200 bg-red-50/30' :
      log.warningLevel === 'check' ? 'border-orange-200 bg-orange-50/30' :
      log.warningLevel === 'caution' ? 'border-amber-200 bg-amber-50/30' :
      'border-slate-100 hover:border-slate-200'
    } cursor-pointer`} onClick={onView}>
      {/* Header: date + field + season + badges */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded px-2 py-0.5 whitespace-nowrap">
              {formatDate(log.ngay)}
            </span>
            {field && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <span className="w-3 h-3 flex items-center justify-center"><i className="ri-map-pin-line" /></span>
                <span className="truncate max-w-[100px]">{field.name}</span>
              </span>
            )}
            {season && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <span className="w-3 h-3 flex items-center justify-center"><i className="ri-seedling-line" /></span>
                <span className="truncate max-w-[100px]">{season.name}</span>
              </span>
            )}
          </div>
          <h4 className="text-sm font-semibold text-slate-900 mt-1 line-clamp-2">{log.hoatDong}</h4>
        </div>

        <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
          {log.isDeleted && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-200 text-slate-600 whitespace-nowrap">Đã xóa</span>
          )}
          {wl.label && !log.isDeleted && (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${wl.bg} ${wl.textColor} whitespace-nowrap`}>
              <span className={`w-3 h-3 flex items-center justify-center mr-1 ${wl.iconColor}`}><i className={wl.icon} /></span>
              {wl.label}
            </span>
          )}
        </div>
      </div>

      {/* Content preview */}
      <p className="text-sm text-slate-700 line-clamp-3 break-words">{log.chiTiet}</p>

      {/* Material tags from materialsNote */}
      {log.materialsNote && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="w-3.5 h-3.5 flex items-center justify-center text-agrida-600 shrink-0"><i className="ri-shopping-bag-line text-xs" /></span>
          <span className="text-xs text-agrida-700 bg-agrida-50 rounded px-2 py-0.5 line-clamp-1 max-w-[250px]">{log.materialsNote}</span>
          {hasMaterialSuggestions && (
            <span className="text-[10px] bg-agrida-100 text-agrida-600 rounded-full px-2 py-0.5 whitespace-nowrap">
              {log.materialSuggestions!.filter((s) => !s.applied).length} gợi ý
            </span>
          )}
        </div>
      )}

      {/* Warning tags */}
      {tags.length > 0 && !log.isDeleted && (
        <div className="flex gap-1.5 flex-wrap">
          {tags.map((tag) => {
            const cfg = WARNING_TAG_CONFIG[tag];
            if (!cfg) return null;
            const levelBg = cfg.level === 'danger' ? 'bg-red-100 text-red-700 border-red-200' :
              cfg.level === 'check' ? 'bg-orange-100 text-orange-700 border-orange-200' :
              'bg-amber-100 text-amber-700 border-amber-200';
            return (
              <span key={tag} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${levelBg} whitespace-nowrap`}>
                <span className="w-3 h-3 flex items-center justify-center"><i className={`${cfg.icon} text-[10px]`} /></span>
                {cfg.label}
              </span>
            );
          })}
        </div>
      )}

      {/* Image thumbnails */}
      {allImages.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4">
          {allImages.slice(0, 4).map((url, idx) => (
            <div key={idx} className="shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-slate-100">
              <img src={url} alt={`thumb-${idx}`} className="w-full h-full object-cover" />
            </div>
          ))}
          {allImages.length > 4 && (
            <div className="shrink-0 w-16 h-16 rounded-xl bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-500">
              +{allImages.length - 4}
            </div>
          )}
        </div>
      )}

      {/* Bottom bar: media indicators + HTX notes + actions */}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
        <div className="flex items-center gap-3 text-xs text-slate-500">
          {allImages.length > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-image-line" /></span>
              {allImages.length}
            </span>
          )}
          {hasVideo && (
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-video-line" /></span>
            </span>
          )}
          {log.ghiAmScript && (
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-mic-line" /></span>
            </span>
          )}
          {log.ghiChuHTX && (
            <span className="flex items-center gap-1 text-sky-600 font-medium">
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-chat-1-line" /></span>
              HTX
            </span>
          )}
        </div>

        {!log.isDeleted && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="text-xs font-medium text-agrida-700 hover:text-agrida-800 flex items-center gap-1 whitespace-nowrap"
            >
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-edit-line" /></span>
              Sửa
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="text-xs font-medium text-red-600 hover:text-red-700 flex items-center gap-1 whitespace-nowrap"
            >
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-delete-bin-line" /></span>
              Xóa
            </button>
          </div>
        )}
      </div>
    </div>
  );
}